import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "../../Components/Grid/Grid";
import Modal from "../../Components/Modal/KnowledgeBase/Modal";
import Button from "../../Components/button/button";
import "../../Style/IncidentView.css";
import config from "../../config";
import getColumnDefs from "../../utils/KnowledgeBase/knowledgeBaseColumnDefs";
import { KnowledgeBaseData } from "../../utils/KnowledgeBase/KnowledgeBaseTypes";
import { formatDuration } from "../../utils/durationUtils";
import { authFetch } from "../../utils/Security/authFetch";
import localforage from "localforage";

const KnowledgeBaseView: React.FC = () => {
  const userId = localStorage.getItem("userid");
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const [rowData, setRowData] = useState<KnowledgeBaseData[]>([]);
  const [filteredData, setFilteredData] = useState<KnowledgeBaseData[]>([]);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] =
    useState<KnowledgeBaseData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const INCIDENT_CACHE_KEY = `incidents_${isAdmin ? "admin" : userId}`;

  const handleEdit = (data: KnowledgeBaseData) => {
    navigate(`/knowledgeBase-edit/${data.ticketId}`, {
      state: { knowledgeBase: data },
    });
  };

  const handleDelete = async (ticketId: string) => {
    if (!window.confirm("Are you sure you want to delete this knowledgeBase?"))
      return;

    try {
      const response = await authFetch(
        `${config.apiBaseUrl}/api/knowledgeBases/${ticketId}`,
        {
          method: "DELETE",
        }
      );

      if (response.ok) {
        const updatedData = rowData.filter(
          (knowledgeBase) => knowledgeBase.ticketId !== ticketId
        );
        setRowData(updatedData);
        setFilteredData(updatedData.filter(applySearchFilter));
      } else {
        console.error("Failed to delete knowledgeBase");
      }
    } catch (error) {
      console.error("Error deleting knowledgeBase:", error);
    }
  };

  useEffect(() => {
     localforage.removeItem(INCIDENT_CACHE_KEY)
    const fetchKnowledgeBases = async () => {
      try {
        const url = isAdmin
          ? `${config.apiBaseUrl}/api/knowledgeBases`
          : `${config.apiBaseUrl}/api/knowledgeBases/user/${userId}`;

        const response = await authFetch(url);
        if (response.ok) {
          const data: KnowledgeBaseData[] = await response.json();

          const formatted = data.map((item) => ({
            ...item,
          }));

          setRowData(formatted);
          setFilteredData(formatted);
        } else {
          console.error("Failed to fetch knowledgeBases");
        }
      } catch (error) {
        console.error("Error fetching knowledgeBases:", error);
      }
    };

    fetchKnowledgeBases();
  }, [isAdmin, userId]);

  const columnDefs = getColumnDefs(handleEdit, handleDelete);

  const handleRowClick = (data: KnowledgeBaseData) => {
    setSelectedKnowledgeBase(data);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedKnowledgeBase(null);
  };

  const handleExportCSV = () => {
    if (!filteredData.length) {
      alert("No data to export.");
      return;
    }

    const headers = [
      "ticketId",
      "ticketCreationDatetime",
      "ticketClosureDatetime",
      "knowledgeBaseStartDatetime",
      "knowledgeBaseCloseDatetime",
      "networkId",
      "siteId",
      "linkId",
      "assetId",
      "teamId",
      "personId",
      "communicationWithCustomer",
      "userid",
      "ticketCreation",
      "ticketClosure",
      "summary",
      "knowledgeBaseStart",
      "knowledgeBaseClose",
      "affectedCustomer",
      "email",
      "affectedNetwork",
      "affectedSite",
      "affectedLink",
      "asset",
      "errorType",
      "closureCode",
      "impact",
      "urgency",
      "priority",
      "responsibleTeam",
      "responsiblePerson",
      "customerComplaint",
      "customerInformed",
      "status",
      "journalUpdate",
      "communication",
      "sla",
      "slaStatus",
      "description",
      "reactionPoint",
      "responseTimeToOpen",
      "responseTimeToClose",
      "acceptanceTime",
      "ola",
      "olaStatus",
      "acceptanceStatus",
    ];

    const csvRows = [
      headers.join(","),
      ...filteredData.map((row) =>
        headers
          .map((field) =>
            JSON.stringify(row[field as keyof KnowledgeBaseData] ?? "")
          )
          .join(",")
      ),
    ];

    const blob = new Blob(["﻿" + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "knowledgeBases.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const applySearchFilter = (item: KnowledgeBaseData): boolean => {
    const query = searchQuery.toLowerCase();
    return Object.values(item).some(
      (value) =>
        value !== null &&
        value !== undefined &&
        value.toString().toLowerCase().includes(query)
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setFilteredData(rowData.filter(applySearchFilter));
  };

  return (
    <>
      <div className="top">
        <div className="add">
          <div className="right-section">
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate("/knowledgeBase-add")}
            >
              Add
            </Button>
            <Button variant="primary" size="md" onClick={handleExportCSV}>
              Export to CSV
            </Button>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                padding: "8px",
                borderRadius: "5px",
                border: "1px solid gray",
                width: "400px",
              }}
            />
          </div>
        </div>
      </div>

      <div className="Grid" style={{ height: "82%" }}>
        <Grid<KnowledgeBaseData>
          rowData={filteredData}
          columnDefs={columnDefs}
          onRowClick={handleRowClick}
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        data={selectedKnowledgeBase}
        onClose={closeModal}
      />
    </>
  );
};

export default KnowledgeBaseView;
